import { useCallback, useEffect, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import useRequests from "../../hooks/http";
import { useTenant } from "../../hooks/useTenant";
import { parseDetail } from "../../utils/errors";

const LABELS: Record<string, string> = {
  password: "Password",
  webauthn: "Security key",
  duo: "Duo",
  "email-otp": "Emailed code",
};

export default function LoginPolicy() {
  const { t } = useTranslation();
  const { activeTenant } = useTenant();
  const { get, patch } = useRequests();

  const [chains, setChains] = useState<string[][]>([]);
  const [available, setAvailable] = useState<string[]>([]);
  const [configured, setConfigured] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = useCallback(async () => {
    if (!activeTenant) return;
    const { data, status } = await get(
      `/admin/tenants/${activeTenant.id}/auth-chains`,
    );
    if (status === 200) {
      setChains(data.chains);
      setAvailable(data.available_factors);
      setConfigured(data.configured);
    }
  }, [activeTenant, get]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (next: string[][] | null) => {
    if (!activeTenant) return;
    setError("");
    setSuccess("");
    const { data, status } = await patch(
      `/admin/tenants/${activeTenant.id}/auth-chains`,
      { chains: next },
    );
    if (status === 200) {
      setSuccess(
        next === null
          ? t("settings.loginPolicy.cleared")
          : t("settings.loginPolicy.saved"),
      );
      return load();
    }
    setError(parseDetail(data?.detail, t("settings.loginPolicy.saveFailed")));
  };

  const addChain = () => setChains((prev) => [...prev, ["password"]]);

  const removeChain = (index: number) =>
    setChains((prev) => prev.filter((_, i) => i !== index));

  const addFactor = (index: number, factor: string) =>
    setChains((prev) =>
      prev.map((chain, i) => (i === index ? [...chain, factor] : chain)),
    );

  const removeFactor = (chainIndex: number, factorIndex: number) =>
    setChains((prev) =>
      prev.map((chain, i) =>
        i === chainIndex ? chain.filter((_, j) => j !== factorIndex) : chain,
      ),
    );

  if (!activeTenant) return null;

  return (
    <Card variant="outlined" sx={{ maxWidth: 600, mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t("settings.loginPolicy.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("settings.loginPolicy.description")}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}
        {!configured && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("settings.loginPolicy.usingDefault")}
          </Alert>
        )}

        <Stack spacing={2} sx={{ mb: 2 }}>
          {chains.map((chain, chainIndex) => (
            <Box
              key={chainIndex}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                p: 1.5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t("settings.loginPolicy.chainLabel", {
                    number: chainIndex + 1,
                  })}
                </Typography>
                <IconButton
                  size="small"
                  aria-label={t("settings.loginPolicy.removeChain")}
                  onClick={() => removeChain(chainIndex)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
                {chain.map((factor, factorIndex) => (
                  <Chip
                    key={`${factor}-${factorIndex}`}
                    label={LABELS[factor] ?? factor}
                    onDelete={() => removeFactor(chainIndex, factorIndex)}
                    size="small"
                  />
                ))}
              </Box>

              <Select
                size="small"
                value=""
                displayEmpty
                onChange={(e) => addFactor(chainIndex, e.target.value as string)}
                renderValue={() => t("settings.loginPolicy.addFactor")}
                sx={{ minWidth: 180 }}
              >
                {available
                  .filter((factor) => !chain.includes(factor))
                  .map((factor) => (
                    <MenuItem key={factor} value={factor}>
                      {LABELS[factor] ?? factor}
                    </MenuItem>
                  ))}
              </Select>
            </Box>
          ))}
        </Stack>

        <Stack direction="row" spacing={2}>
          <Button startIcon={<AddIcon />} onClick={addChain}>
            {t("settings.loginPolicy.addChain")}
          </Button>
          <Button variant="contained" onClick={() => save(chains)}>
            {t("settings.loginPolicy.save")}
          </Button>
          <Button color="error" onClick={() => save(null)} disabled={!configured}>
            {t("settings.loginPolicy.reset")}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
